'use client'

import { useEffect, useRef, useState } from 'react'
import { runRingFrames } from 'src/components/ShowRaceStatus/statusRing'
import { crashHeatFor } from './crashHeat'
import { datadogLogs } from '@datadog/browser-logs'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import { getLatestEvent } from 'src/utils/raceUtils'
import type { MarbleGame } from 'src/types/domain/Game'
import { getBettingTimeWindow } from 'src/utils/getBettingTimeWindow'
import { getSettledTimeWindow } from 'src/utils/getSettledTimeWindow'
import { hardCloseClock, hardCountdown, perfNow } from 'src/utils/hardCloseClock'
import { publishDisplayMultiplier, setBeaconRound, useFrameBeacon } from './useFrameBeacon'
import { resolveCrashHold, useVisualCrashStatus } from './useVisualCrash'
import { resolveCrashEventInfo } from './crashEventInfo'
import {
  resolveLiveHeroMultiplier,
  resolveHeroStamp,
  resolveDisplayTime,
  holdMonotonicLiveValue,
  shouldLogHeroOvershoot,
  heroOvershoot
} from './crashHeroClamp'
import { BEACON_FRESH_MS, isBeaconDead, isBeaconFresh } from './crashFrameBeacon'
import {
  crashEventClockOriginMs,
  crashLockArming,
  crashRoundHoldS,
  lockBeatSeconds,
  lockGapBarFill
} from './crashHold'
import {
  CRASH_LATCH_UNKNOWN_ROUND,
  CRASH_REVEAL_DELAY_SECONDS,
  crashLatchEngaged,
  releaseCrashLatch,
  resolveDisplayPhase,
  type CrashDisplayPhase,
  holdLivePhase
} from './crashDisplayPhase'
import {
  formatCountdownSeconds,
  getRaceCountdownInfo,
  resolveCenterCountdown
} from './raceCountdown'
import './crash-multiplier-display.scss'

// Crash-curve contract — same numbers as `useCrashMultiplier` (and the
// 155-game-crash engine / marbles-stack-go backend). This component runs its
// OWN single rAF loop and writes the value imperatively, so it deliberately
// does not consume the hook (which drives React state per frame for the
// betting logic) — but the formula MUST stay identical. The climb curve and
// the never-overshoot clamp both live in ./crashHeroClamp so they're pure +
// tested (B2); this component just drives them per frame.

type Props = Readonly<{
  game: MarbleGame
  // Dev-harness escape hatch (default true — production behaviour): whether
  // this instance PUBLISHES the shared display clock
  // (publishDisplayMultiplier). The /dev/crash-panel harness mounts several
  // displays side by side; if they all published, the pinned arming tile's
  // flat 1.00x would poison the clock every riding zone tile reads. The
  // real page mounts exactly ONE display, which must keep publishing.
  publishClock?: boolean
}>

/**
 * The countdown (v36): a slim horizontal DEPLETION BAR + numeral — the one
 * countdown instrument on every breakpoint (the circular ring retired;
 * Kasper preferred the bar). The fill DEPLETES via width%, stepped at the
 * 1Hz tick and smoothed by a 1s linear transition into one continuous
 * sweep; the numeral re-keys per second so each digit settles with a soft
 * 180ms tick beat; the final seconds (≤3) warm bar + numeral to the armed
 * amber — emphasis, never an alarm flash. Reduced-motion stills the fill
 * transition; the numeral alone carries the countdown. Presentation only —
 * what the timer counts (getRaceCountdownInfo) is untouched.
 *
 * Exported for the dev harness (/dev/crash-panel), which captures the
 * betting + settling states without a live round.
 */
export function CountdownRing({
  seconds,
  progress,
  small,
  urgent: urgentOverride,
  barRef
}: Readonly<{
  /** Null: the bar alone, no digits (a short-hold round's gap from the lock to the 3-2-1). */
  seconds: number | null
  progress: number
  small?: boolean
  /** Overrides "the final 3 s": the gap keeps the warm amber from the last seconds of betting into the 3-2-1. */
  urgent?: boolean
  /**
   * A hard round's bar, drawn every frame by the display (`--crash-bar-left`),
   * so it empties exactly at the lock and at the beat, and a new count starts
   * full at once instead of sweeping back over the 1 s transition.
   */
  barRef?: React.Ref<HTMLDivElement>
}>) {
  const urgent = urgentOverride ?? (seconds !== null && seconds <= 3)
  const pct = Math.min(Math.max(progress, 0), 100)
  const fillStyle = barRef ? { width: 'var(--crash-bar-left)' } : { width: `${100 - pct}%` }
  return (
    // v36 — the horizontal DEPLETION BAR is THE countdown everywhere
    // (Kasper loved it on mobile; the ring retired). Slim track + numeral,
    // 1Hz progress smoothed by a 1s linear transition, amber in the final
    // seconds.
    <div
      className={[
        'crash-mult__timer',
        small ? 'crash-mult__timer--small' : '',
        urgent ? 'crash-mult__timer--urgent' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* v37 — TWIN bars: the numeral sits centre, one half-bar each side,
          both retracting TOWARD it as time drains — the countdown visibly
          closes in on the number. Each half carries the same remaining%. */}
      <div
        className={barRef ? 'crash-mult__bar crash-mult__bar--framed' : 'crash-mult__bar'}
        ref={barRef}
        style={
          barRef ? ({ '--crash-bar-left': `${100 - pct}%` } as React.CSSProperties) : undefined
        }
      >
        <span className="crash-mult__bar-track crash-mult__bar-track--left">
          <span className="crash-mult__bar-fill" style={fillStyle} />
        </span>
        {/* Re-keyed per second: the soft tick-settle replays on each digit. */}
        {seconds !== null && (
          <span key={seconds} className="crash-mult__bar-number">
            {formatCountdownSeconds(seconds)}
          </span>
        )}
        <span className="crash-mult__bar-track crash-mult__bar-track--right">
          <span className="crash-mult__bar-fill" style={fillStyle} />
        </span>
      </div>
    </div>
  )
}

// heat ramp shared with the social-focus readout so they never drift (v19)

/**
 * THE multiplier — the one number on screen. The video no longer bakes in any
 * game text; this component owns the multiplier and all status text, layered
 * upper-centre over the CCTV footage (pointer-events: none).
 *
 * Clock: every frame carries a machine-readable time beacon baked into its
 * pixels (engine lockdown report §9). While that lock is fresh the display
 * clock is `beacon.t + ageMs/1000` — the race time of the exact frame THIS
 * viewer is seeing, extrapolated between 8Hz samples so the number still
 * ticks at 60fps. A briefly stale beacon HOLDS from its origin (anti-jerk);
 * only a DEAD beacon (or none this round) falls back to the race_started
 * event clock — see resolveDisplayTime. This loop also PUBLISHES the display clock's multiplier
 * (publishDisplayMultiplier) so every player-facing number — the cash-out
 * bar's live amount and "@ x.xx" — reads the SAME clock (bug c). Payouts are
 * untouched: cash-outs settle backend-side on the event clock, and since
 * frames trail the backend the paid multiplier is ≥ the one shown.
 *
 * Perf contract: ONE requestAnimationFrame loop while a round is engaged,
 * writing `textContent` / `data-heat` via a ref — ZERO React state updates
 * per tick (setState fires only on phase flips and countdown digits, a
 * handful per round). No rAF runs during betting/idle.
 *
 * Phases (resolveDisplayPhase — pure, tested), a CONTINUOUS narrative:
 *   - betting → a quiet "Place your bets" stage-setter through the betting
 *     window AND the pre-roll footage (beacon t < 0) — no blank gaps.
 *   - arming  → t in [0, 3): "TRACKING ARMED" flickers on, then a big 3‥2‥1
 *     countdown (one number per second, each snapping in), then…
 *   - live    → the multiplier materialises at 1.00x and climbs, breathing
 *     subtly, heating up by value. Once the crash EVENT is known the number
 *     caps at M — it must never overshoot the crash point.
 *   - crash   → the number snaps red at the crash multiplier, a mono
 *     "CRASHED" stamp slides in above it, one 300ms transform-only shake —
 *     then both STAY on screen until the next round's betting opens
 *     (race_ready), where they crossfade into "Place your bets". With a
 *     live beacon the snap HOLDS until the viewer's frame clock reaches
 *     t_crash = 3 + ln(M)/rate — the stamp lands on the frame where the
 *     crash is visible, not when the event packet arrives. A beacon that
 *     died mid-round gets a 5s return-grace (hold the live look, never
 *     stamp a crash on frames that haven't shown it); one that never lived
 *     this round stamps immediately on the event clock. Hard cap: t_crash-
 *     equivalent + 8s (resolveCrashHold — shared with useVisualCrash).
 *   - hidden  → nothing to narrate (no round, no betting, no held crash).
 */
export function CrashMultiplierDisplay({ game, publishClock = true }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())

  const race = game.races[0]
  const raceEvents = race?.raceEvents ?? []

  // ONE crash extraction (pure, tested): scans ALL crash-announcing events
  // for the first USABLE multiplier, so a payload-less race_crashed can
  // never mask race_ended's settled M (stamp-flash fix).
  const { crashKnown, crashedMultiplier } = resolveCrashEventInfo(raceEvents)

  const latestEventType = getLatestEvent(raceEvents)?.eventType
  const isClimbing = latestEventType === 'race_started'
  // Betting window open — the "Place your bets" stage-setter (v6).
  const isBettingOpen = latestEventType === 'race_ready' || latestEventType === 'race_starting'
  // The event clock's origin: race_started's stamp, moved back by 3 − R on a
  // short-hold round (crashHold.ts), so it reads the beacon's clock. At R 3
  // (and on every round that is not hard) it is the stamp itself.
  const startTs = crashEventClockOriginMs(raceEvents)

  const { getBeaconTime, wake } = useFrameBeacon()

  // Round-scope the beacon (bug a): a round change invalidates the lock so
  // the OLD round's still-streaming tail can never drive the NEW round's
  // display, and race_started's timestamp gives the decoder the event-clock
  // reference its plausibility bound is checked against.
  const roundId = race?.uuid
  useEffect(() => {
    setBeaconRound(roundId, startTs)
  }, [roundId, startTs])

  // ── Same-round crash latch (v9, defensive) ─────────────────────────────
  // Once this display ENTERS crash for a race uuid, nothing may flip it
  // back to live/arming for that SAME uuid — not event-list churn (the
  // crashed event dropping out / a late race_ready replay re-sorting to
  // latest), not a beacon oscillation re-reading below t_crash after a loop
  // restart. Only a genuinely NEW race uuid releases the latch; undefined
  // uuids (races[] churn) keep it engaged. The multiplier is latched with
  // it so the held "CRASHED @ M" text can't drift if the event churns away.
  // This effect is declared BEFORE the rAF-loop effect on purpose: on a
  // round change it releases the latch before the loop re-evaluates.
  const crashLatchRef = useRef<string | null>(null)
  const crashLatchMultiplierRef = useRef<number | null>(null)
  // B2 telemetry bookkeeping: the max multiplier this viewer actually SAW
  // this round, and a one-shot guard so the overshoot log fires at most once
  // per round. Reset together with the latch on a genuinely new round.
  const shownMaxRef = useRef(1)
  const overshootLoggedRef = useRef(false)
  useEffect(() => {
    const released = releaseCrashLatch(crashLatchRef.current, roundId)
    if (released === null) {
      crashLatchMultiplierRef.current = null
      shownMaxRef.current = 1
      overshootLoggedRef.current = false
    }
    crashLatchRef.current = released
  }, [roundId])

  // Betting frames carry no beacon (the sampler parks itself dead between
  // rounds) — nudge it awake the moment race_started arrives so the first
  // climbing frames lock immediately.
  useEffect(() => {
    if (isClimbing) wake()
  }, [isClimbing, wake])

  const engaged = crashKnown || isClimbing

  const [displayPhase, setDisplayPhase] = useState<CrashDisplayPhase>('hidden')
  const phaseRef = useRef<CrashDisplayPhase>('hidden')
  const [count, setCount] = useState(3)
  const valueRef = useRef<HTMLDivElement>(null)
  // v35 — the HUD stage frame tints with the heat: the rAF mirrors the
  // value's data-heat onto the root (tier flips only, never per frame), and
  // the frame CSS reads it from there.
  const rootRef = useRef<HTMLDivElement>(null)

  // ── The centre countdown (v7): betting ring / "Next round" ring ───────
  // Which countdown the current phase carries (pure, tested); remaining
  // seconds derive EXACTLY like ShowRaceStatus's pill — same events, same
  // getBettingTimeWindow / getSettledTimeWindow config — via the shared
  // getRaceCountdownInfo. Ticks at 1Hz (one setState per second, no rAF).
  // The ring now starts at race_ended (post_hold after the crash) rather than
  // waiting for race_settled. race_ended is scheduled against the crash frame
  // as the ENGINE pushes it to the wire, not against this viewer's delivery —
  // so a player far enough behind the stream could still be mid-climb when it
  // lands. While their own visual crash is still 'armed', suppress the
  // between-rounds ring: announcing "Next round" over a climbing multiplier is
  // the same spoiler holdCrashPill exists to prevent. Betting-phase rings are
  // unaffected.
  // Scoped to race_ended / race_settled: a race_cancelled round never produces
  // a crash frame, so 'armed' can persist and would strand the centre blank.
  const visualCrashStatus = useVisualCrashStatus()
  const resolvedCountdownKind = resolveCenterCountdown(latestEventType)
  const countdownKind =
    resolvedCountdownKind === 'next-round' &&
    visualCrashStatus === 'armed' &&
    latestEventType !== 'race_cancelled'
      ? null
      : resolvedCountdownKind
  const [countdownNowMs, setCountdownNowMs] = useState(() => Date.now())
  useEffect(() => {
    if (countdownKind === null) return undefined
    setCountdownNowMs(Date.now())
    const id = setInterval(() => setCountdownNowMs(Date.now()), 1000)
    return () => clearInterval(id)
  }, [countdownKind, latestEventType])

  // A hard round's betting ring counts to the board's lock, then X (DESIGN §6);
  // re-read at the same 1 Hz tick. Null on every other round: today's ring.
  const hardClock = countdownKind === 'betting' ? hardCloseClock(race, game.races[1]) : null
  const hardCount = hardClock ? hardCountdown(hardClock, perfNow()) : null

  // Crash rev 4.1: a hard round whose race_started fires 3 − R into the clip's
  // beat (R < 3) plays the start of that beat inside the lock window, so its
  // 3-2-1 runs from the lock signal, not from race_started (crashHold.ts).
  // Off on every other round, and at R 3: today's narrative.
  const holdS = crashRoundHoldS(raceEvents)
  const lockArming =
    !engaged &&
    isBettingOpen &&
    hardClock !== null &&
    hardClock.lockedBy !== undefined &&
    holdS < CRASH_REVEAL_DELAY_SECONDS
  const lockArmingAt = lockArming ? hardClock?.lockedAt : undefined
  const lockArmingLeadS = lockArming ? (hardClock?.close.lockLeadS ?? 0) : 0

  const countdown =
    countdownKind !== null
      ? getRaceCountdownInfo({
          hard: hardCount,
          raceEvents,
          nowMs: countdownNowMs,
          bettingWindowSec: getBettingTimeWindow(game),
          settledWindowSec: getSettledTimeWindow(game),
          // Crash-only surface: race_ended is the between-rounds anchor here.
          betweenRoundsAnchorsOnEnded: true
        })
      : null

  // A hard round's bar is drawn every frame (as the status pill's ring,
  // statusRing.ts): to the lock, empty exactly at it, and the label, digits
  // and stage re-render on the frame they change rather than at the next 1 Hz
  // tick. From the lock on a short-hold round the beat loop below draws it.
  const barRef = useRef<HTMLDivElement>(null)
  const hardBarKey = hardCount ? `${hardCount.stage}|${hardCount.current}` : ''
  const hardBarFramed = countdownKind === 'betting' && hardCount !== null && !engaged && !lockArming
  useEffect(() => {
    if (!hardBarFramed) return undefined
    const previousRace = game.races[1]
    return runRingFrames({
      read: () => {
        const now = perfNow()
        const clock = hardCloseClock(race, previousRace, now)
        const count = clock ? hardCountdown(clock, now) : null
        return {
          ring: count ? 100 - count.progress : 0,
          key: count ? `${count.stage}|${count.current}` : ''
        }
      },
      initialKey: hardBarKey,
      draw: (left) => {
        barRef.current?.style.setProperty('--crash-bar-left', `${left}%`)
      },
      render: () => {
        setCountdownNowMs(Date.now())
      }
    })
  }, [hardBarFramed, hardBarKey, race, game.races])
  // The gap from the lock to the 3-2-1 (short hold): the bar alone, full at
  // the lock and empty as the 3 appears; its first frame before the loop runs.
  const lockGapLeft = lockArming
    ? lockGapBarFill({
        beatS: lockBeatSeconds({
          lockedAt: lockArmingAt,
          lockLeadS: lockArmingLeadS,
          holdS,
          now: perfNow()
        }),
        lockLeadS: lockArmingLeadS,
        holdS
      })
    : undefined

  // THE loop — one rAF while a round is engaged (climbing or crash pending),
  // and from the lock on a short-hold round (above). No rAF runs during
  // betting/idle otherwise (perf contract unchanged).
  useEffect(() => {
    const setPhase = (p: CrashDisplayPhase) => {
      if (phaseRef.current !== p) {
        phaseRef.current = p
        setDisplayPhase(p)
      }
    }

    // v9 latch: this round's crash already landed on this screen — park on
    // the stamp unconditionally. No event churn, beacon oscillation or
    // same-uuid race_ready replay may re-enter live/arming; the release
    // effect above already cleared the latch if a NEW uuid arrived.
    if (crashLatchEngaged(crashLatchRef.current, roundId)) {
      const heldM =
        typeof crashedMultiplier === 'number' && Number.isFinite(crashedMultiplier)
          ? crashedMultiplier
          : crashLatchMultiplierRef.current
      // The authoritative M refreshes the latch (a stamp frozen on the
      // last-live fallback settles onto the settled M; later event churn
      // then can't drift the held text back to the fallback).
      if (typeof heldM === 'number') crashLatchMultiplierRef.current = heldM
      if (publishClock && typeof heldM === 'number') publishDisplayMultiplier(heldM)
      setPhase('crash')
      return
    }

    if (!engaged && lockArming) {
      // From the lock to race_started on a short-hold round: the beat's clock
      // is the beacon while it reads the frames, else the lock's own count
      // (the same resolveDisplayTime as below). The stage-setter holds until
      // the beat starts, then 3‥2‥1, held on 1 until race_started engages
      // the loop below. Nothing climbs before race_started.
      let preStartRaf = 0
      const preStart = () => {
        const beatS = resolveDisplayTime(
          getBeaconTime(),
          BEACON_FRESH_MS,
          lockBeatSeconds({
            lockedAt: lockArmingAt,
            lockLeadS: lockArmingLeadS,
            holdS,
            now: perfNow()
          })
        )
        const gapLeft = lockGapBarFill({ beatS, lockLeadS: lockArmingLeadS, holdS })
        if (gapLeft !== undefined) {
          barRef.current?.style.setProperty('--crash-bar-left', `${gapLeft}%`)
        }
        const arming = crashLockArming(beatS)
        if (arming?.phase === 'arming') {
          setPhase('arming')
          if (publishClock) publishDisplayMultiplier(1)
          setCount((prev) => (arming.count === prev ? prev : arming.count))
        } else {
          setPhase('betting')
        }
        preStartRaf = requestAnimationFrame(preStart)
      }
      preStart()
      return () => cancelAnimationFrame(preStartRaf)
    }

    if (!engaged) {
      // Idle narration: "Place your bets" when the window is open; a held
      // crash stamp persists through the race_scheduled gap; else hidden.
      setPhase(
        resolveDisplayPhase({
          engaged: false,
          isBettingOpen,
          crashStamped: false,
          tDisplay: undefined,
          prevPhase: phaseRef.current
        })
      )
      return
    }

    let raf = 0
    let lastText = ''
    let lastHeat = ''
    // Grace bookkeeping for resolveCrashHold. Effect-local on purpose: the
    // effect tears down on round/crash-event changes, which is exactly the
    // per-round lifetime the grace needs.
    let graceStartMs: number | null = null
    // Grace bookkeeping for resolveHeroStamp (stamp-flash fix): when the
    // crash is known but M is unrevealed, the stamp defers — this tracks
    // how long it has been deferring. Effect-local like graceStartMs; the
    // effect re-runs the moment crashedMultiplier lands, which resets it.
    let noMSinceMs: number | null = null

    const frame = () => {
      // ── the display clock: beacon (fresh OR stale-held) > event clock ──
      // A merely STALE beacon holds from its own origin (anti-jerk); the event
      // clock is used before the first lock AND once the beacon is DEAD past
      // BEACON_DEAD_MS — the full contract lives on resolveDisplayTime.
      const beacon = getBeaconTime()
      const beaconFresh = isBeaconFresh(beacon)
      const eventElapsed = startTs !== undefined ? (Date.now() - startTs) / 1000 : undefined
      const tDisplay = resolveDisplayTime(beacon, BEACON_FRESH_MS, eventElapsed)

      // ── crash sync (bug b) ──
      if (crashKnown) {
        // Shared decision with useVisualCrash: stamp at the viewer's crash
        // frame when the beacon is live; when the lock died mid-round, hold
        // the live look (capped at M below) through a return-grace instead
        // of stamping on the event clock over frames that haven't shown the
        // crash yet; stamp immediately only if the beacon never lived this
        // round — hard-capped at t_crash-equivalent + 8s.
        const decision = resolveCrashHold({
          nowMs: Date.now(),
          startTsMs: startTs,
          beacon,
          crashedMultiplier,
          graceStartMs
        })
        graceStartMs = decision.graceStartMs
        if (decision.stamp) {
          // Stamp-flash fix: the stamped NUMBER is final the moment it is
          // shown, so the phase may only park on 'crash' when a trustworthy
          // multiplier exists — the event's M (normal path), the latched M
          // (event churned out), or, after the no-M grace, the frozen
          // last-live value. While M is unrevealed (payload-less crash
          // event) the stamp DEFERS and the live look below holds the
          // frozen value — never the old `?? 1` floor's "1.00x".
          const stampDecision = resolveHeroStamp({
            crashedMultiplier,
            latchedMultiplier: crashLatchMultiplierRef.current,
            shownMax: shownMaxRef.current,
            nowMs: Date.now(),
            noMSinceMs
          })
          noMSinceMs = stampDecision.noMSinceMs
          if (stampDecision.stamp) {
            // Land the display clock on the stamped value for any bar still
            // reading it, and latch it — the held "CRASHED @ M" text always
            // freezes on a recorded multiplier from here on.
            if (publishClock) publishDisplayMultiplier(stampDecision.multiplier)
            crashLatchMultiplierRef.current = stampDecision.multiplier
            // Engage the v9 latch for THIS uuid: from here the phase can
            // only leave crash via a genuinely NEW race uuid.
            crashLatchRef.current = roundId ?? CRASH_LATCH_UNKNOWN_ROUND
            // The loop ends here — the crash fade is timeout-driven.
            setPhase('crash')
            return
          }
        }
        // This viewer hasn't SEEN the crash frame yet (or M is unrevealed) —
        // keep the live look below: capped at M when known, frozen at the
        // last shown value when not.
      }

      if (tDisplay === undefined) {
        raf = requestAnimationFrame(frame)
        return
      }

      // One-way live floor (holdLivePhase): a backward display-clock step —
      // the dead-beacon fallback engaging, or a decoder re-lock stepping back
      // by the stream lag — must not re-mount the countdown mid-climb. Routed
      // through the live branch, F5 keeps the published value at shownMax.
      const candidatePhase: CrashDisplayPhase =
        tDisplay < 0 ? 'betting' : tDisplay < CRASH_REVEAL_DELAY_SECONDS ? 'arming' : 'live'
      const flooredPhase = holdLivePhase(candidatePhase, phaseRef.current)

      if (flooredPhase === 'betting') {
        // Pre-roll footage (the beacon runs negative) — the stage-setter
        // holds so the narrative never blanks between betting and arming.
        setPhase('betting')
        if (publishClock) publishDisplayMultiplier(1)
      } else if (flooredPhase === 'arming') {
        setPhase('arming')
        if (publishClock) publishDisplayMultiplier(1) // flat at 1.00x through the reveal hold
        const digit = Math.max(1, Math.min(3, Math.ceil(CRASH_REVEAL_DELAY_SECONDS - tDisplay)))
        setCount((prev) => (digit === prev ? prev : digit))
      } else {
        setPhase('live')
        // B2 (F1+F2+F3): the shown value is UNCONDITIONALLY bounded by the
        // crash point the backend will settle — the live event's M, or the
        // latched M if the event churns out (F2) — and a stale-beacon event
        // clock that ran past the crash is held at t_crash (F3). The hero can
        // therefore NEVER read above M, so the stamp lands on M with no
        // visible downward correction (the 2.43x→2.12x bug). F4: a crash
        // event with NO usable M freezes the hero at the last shown value —
        // neither climbing blind past the real crash point nor resetting.
        const rawMult = resolveLiveHeroMultiplier({
          tDisplay,
          beaconFresh,
          beaconDead: isBeaconDead(beacon),
          crashedMultiplier,
          latchedMultiplier: crashLatchMultiplierRef.current,
          crashKnown,
          shownMax: shownMaxRef.current
        })
        // F5 (v47): never render below the round's shown max — the mid-round
        // join rode the event clock (leads the frames ~1.6-2.3s) until the
        // first beacon lock stepped the clock back and the number visibly
        // dropped. Held, it pauses ≈ the sync delta and resumes climbing.
        const mult = holdMonotonicLiveValue(rawMult, shownMaxRef.current)
        if (mult > shownMaxRef.current) shownMaxRef.current = mult
        // Publish THE display clock (bug c) — the cash-out bar reads this so
        // its amount and "@ x.xx" always agree with the hero.
        if (publishClock) publishDisplayMultiplier(mult)
        const el = valueRef.current
        if (el) {
          const text = `${mult.toFixed(2)}x`
          if (text !== lastText) {
            lastText = text
            el.textContent = text
          }
          const heat = crashHeatFor(mult)
          if (heat !== lastHeat) {
            lastHeat = heat
            el.dataset.heat = heat
            // Mirror onto the root so the stage frame tints in step (v35).
            if (rootRef.current) rootRef.current.dataset.heat = heat
          }
        }
      }
      raf = requestAnimationFrame(frame)
    }

    frame()
    return () => cancelAnimationFrame(raf)
  }, [
    engaged,
    crashKnown,
    startTs,
    crashedMultiplier,
    getBeaconTime,
    isBettingOpen,
    roundId,
    publishClock,
    lockArming,
    lockArmingAt,
    lockArmingLeadS,
    holdS
  ])

  // NOTE (v6): the old ~1.5s crash fade is GONE — the stamp + red number
  // stay on screen until the next round's betting opens, where the idle
  // branch above crossfades them into "Place your bets".

  // ── B2 overshoot telemetry (one-shot per round) ────────────────────────
  // The clamp above should mean the hero NEVER reads above M — but if it
  // ever does (a clock/beacon edge the guards miss), we want to see it. When
  // race_ended lands, compare the max value this viewer actually SAW against
  // the settled M; log once if it overshot by more than the threshold. Pure
  // observability — it changes nothing on screen.
  useEffect(() => {
    if (overshootLoggedRef.current) return
    if (typeof crashedMultiplier !== 'number' || !Number.isFinite(crashedMultiplier)) return
    const shownMax = shownMaxRef.current
    if (!shouldLogHeroOvershoot(shownMax, crashedMultiplier)) return
    overshootLoggedRef.current = true
    const beacon = getBeaconTime()
    const beaconFresh = isBeaconFresh(beacon)
    const eventLag =
      startTs !== undefined && beacon !== null
        ? (Date.now() - startTs) / 1000 - (beacon.t + beacon.ageMs / 1000)
        : null
    datadogLogs.logger.warn('crash_hero_overshoot', {
      shownMax,
      m: crashedMultiplier,
      delta: heroOvershoot(shownMax, crashedMultiplier),
      beaconFresh,
      eventLag
    })
  }, [crashedMultiplier, roundId, getBeaconTime, startTs])

  // v35 — the stage frame's heat resets with the round (outside live/crash
  // the frame rests neutral; the live rAF re-tints it per tier).
  useEffect(() => {
    if (displayPhase === 'live' || displayPhase === 'crash') return
    if (rootRef.current) rootRef.current.dataset.heat = 'cool'
  }, [displayPhase])

  // The between-rounds beat (race_settled / race_cancelled): "Next round" +
  // ring, mirroring the pill's settled countdown — rendered under a held
  // CRASHED stamp, or alone when there is no stamp to hold, so the centre
  // narrative never goes blank between rounds.
  const nextRoundBlock =
    // v36 — no "Next round" wordmark: the depleting bar IS the message
    // (Kasper: easily understandable without the label).
    countdownKind === 'next-round' && countdown !== null ? (
      <div className="crash-mult__next">
        <CountdownRing seconds={countdown.current} progress={countdown.progress} small />
      </div>
    ) : null

  if (displayPhase === 'hidden' && nextRoundBlock === null) return null

  // Held stamp text: the event's M, else the LATCHED M (v9 — if the crash
  // event churns out of the list the held number must not start re-growing
  // from the wall clock). B2 (F2): there is NO wall-clock recompute fallback
  // — a crash stamp is only ever rendered after the loop latched M. F4
  // (stamp-flash fix): the stamp path now ALWAYS records a multiplier
  // before parking on 'crash' (the event/latched M, or — after the no-M
  // grace — the frozen last-live value via resolveHeroStamp), so the bare
  // 1.00x floor below is unreachable, kept only for type-total safety. It
  // can never flash again: while M is unrevealed the phase stays live,
  // holding the frozen value, and settles onto the authoritative M the
  // instant it lands.
  const crashedText = `${(crashedMultiplier ?? crashLatchMultiplierRef.current ?? 1).toFixed(2)}x`

  return (
    // The --crashed-beat modifier drives the one-shot CCTV "signal cut"
    // scanline flash + impact composition (v34) — presentation only, and
    // only ever set by the SAME visually-gated phase machine as the stamp.
    <div
      className={`crash-mult ${displayPhase === 'crash' ? 'crash-mult--crashed-beat' : ''} crash-mult--phase-${displayPhase}`}
      ref={rootRef}
      aria-hidden
    >
      {/* v35 — THE STAGE: every phase renders inside one content-hugging
          lockup wearing the game's tracking-bracket frame (the chips'
          visual language, scaled up). The frame corners hug whatever the
          phase shows, tint with the live heat (root data-heat), and flash
          red on the crash beat — the hero is a designed HUD instrument,
          not floating text. */}
      <div className="crash-mult__stage">
        <span aria-hidden className="crash-mult__stage-frame" />

        {displayPhase === 'betting' && (
          <>
            <span className="crash-mult__betting">
              {hardCount?.stage === 'sending' ? t('Locked') : t('Place your bets')}
            </span>
            {/* The betting ring holds only through the actual window (kind
                'betting') — the pre-roll footage keeps the stage-setter text
                alone, exactly the old narrative. */}
            {countdownKind === 'betting' && countdown !== null && (
              <CountdownRing
                barRef={hardBarFramed || lockArming ? barRef : undefined}
                progress={lockArming ? 100 - (lockGapLeft ?? 0) : countdown.progress}
                seconds={lockArming ? null : countdown.current}
                urgent={lockArming ? true : undefined}
              />
            )}
          </>
        )}

        {displayPhase === 'arming' && (
          <>
            <span className="crash-mult__eyebrow">{t('Tracking armed')}</span>
            {/* key re-mounts the digit each second so the snap-in replays */}
            <span key={count} className="crash-mult__count">
              {count}
            </span>
          </>
        )}

        {displayPhase === 'live' && (
          <div ref={valueRef} className="crash-mult__value" data-heat="cool">
            1.00x
          </div>
        )}

        {displayPhase === 'crash' && (
          <>
            <span className="crash-mult__stamp">{t('Crashed')}</span>
            <div className="crash-mult__value crash-mult__value--crashed">{crashedText}</div>
            {nextRoundBlock}
          </>
        )}

        {/* No held crash stamp between rounds (viewer joined mid-gap, or the
            round was cancelled) → the "Next round" beat carries the centre. */}
        {displayPhase === 'hidden' && nextRoundBlock}
      </div>
    </div>
  )
}
